import { z } from 'zod';
import { LocalizedLabelSchema } from './localized';

/**
 * Authorization vocabulary (SEC-023, SEC-024, SEC-026).
 *
 * Permissions are **granular verbs per resource**, never coarse role names: an authorization check
 * evaluates permissions only (SEC-025, ADR-0006), so changing a role's composition cannot silently
 * change what a check means.
 *
 * This catalog covers the resources that exist today. Later phases extend it; nothing here encodes a
 * business role list, which is a stakeholder input (`SD-02`).
 */
export const PERMISSIONS = [
  // Audit (AUDIT-004: reading audit data is itself a sensitive read)
  'audit.view',
  'audit.export',
  /** Reveals the before/after change summary of an audit record. */
  'audit.viewChanges',
  /** Reveals request context (IP, user agent) on an audit record. */
  'audit.viewContext',

  // Approvals (APPROVAL-001 … APPROVAL-007). Breadth comes from the data scope, as everywhere else
  // (ADR-0006): there is deliberately no "viewAny" permission, because a second breadth mechanism
  // alongside scopes is how the two drift apart.
  'approval.policy.view',
  /** Create or edit a **draft** policy. A published version is immutable (APPROVAL-006). */
  'approval.policy.create',
  /** Publish a draft, which makes it the version live requests are bound to. Administrative. */
  'approval.policy.publish',
  'approval.request.create',
  'approval.request.view',
  /** Reveals the monetary and percentage context of a request; financial values are field-controlled. */
  'approval.request.viewAmounts',
  'approval.request.approve',
  'approval.request.reject',
  /** Cancel someone else's request within scope. A requester may always cancel their own. */
  'approval.request.cancel',
  /** Move a pending stage to another approver — the offboarding path (APPROVAL-007). Administrative. */
  'approval.request.reassign',
  /** Escalate overdue stages to the direct manager (APPROVAL-005). Administrative. */
  'approval.request.escalate',
  /** Delegate your own approval authority, time-bounded (APPROVAL-004). */
  'approval.delegation.manage',
  /** Create or revoke a delegation on behalf of another account. Administrative. */
  'approval.delegation.manageAny',

  // Security accounts (SEC-011 … SEC-022). Administration only: a person needs no permission to
  // manage their own sessions or confirm their own password.
  'security.account.view',
  'security.account.create',
  'security.account.suspend',
  'security.account.reactivate',
  'security.account.offboard',
  /** Issue a password-reset token for another account, to be delivered out of band. */
  'security.account.resetPassword',
  'security.session.viewAny',
  'security.session.revokeAny',

  // Deployment company profile and branding (PLAT-022, PLAT-023, ADR-0027)
  'company.profile.view',
  /** Change the company's identity, contact details, colour or images. Administrative. */
  'company.profile.manage',

  // Settings, reference data and feature flags (PLAT-024 … PLAT-026)
  'settings.view',
  /** Change a business setting or a feature flag. Administrative. */
  'settings.manage',
  /** Add, relabel, reorder, deactivate reference items; add tax rates. Administrative. */
  'referenceData.manage',

  // Number sequences (CORE-DOC-001). Issuing is a module operation, never a route.
  'numbering.view',
  /** Define and activate a document number format. Administrative. */
  'numbering.manage',

  // Documents and templates (CORE-DOC-002, 004, 006). Breadth is the owning record's scope.
  'document.view',
  'document.upload',
  /** Obtain a short-lived link to a document's file. Every issue is audited (CORE-DOC-006). */
  'document.download',
  'document.archive',
  /**
   * Generate a business PDF — quotation, reservation, contract summary, schedule, receipt, statement —
   * from a record the actor can read (CORE-DOC-003). Each generation is a new, audited version.
   */
  'document.generate',
  /** Revoke an issued document, so its verification page reports it revoked (CORE-DOC-005). Administrative. */
  'document.revoke',
  /** Set a document's retention date or legal hold. Administrative. */
  'document.manageRetention',
  'template.view',
  /** Write, publish and retire templates — the wording documents are issued in. Administrative. */
  'template.manage',

  // Notifications (CORE-NOTIFY). A person needs no permission for their own inbox.
  /** See the delivery state of every notification — operations, not content. Administrative. */
  'notification.viewDeliveries',
  /** Run the delivery sweep by hand. Administrative. */
  'notification.dispatch',

  // Tasks (CORE-TASK). A person needs no permission for tasks assigned to, escalated to, or created by them.
  /** See every task inside one's data scope, not only one's own. */
  'task.view',
  'task.create',
  /** Edit, reassign, cancel and reopen tasks inside one's data scope, not only those one created. */
  'task.manage',
  /** Move all of a person's open tasks to someone else, e.g. at offboarding. Administrative. */
  'task.reassign',
  /** Run the reminder and escalation sweep by hand. Administrative. */
  'task.sweep',

  // Integrations (INTEGRATION-001 … 005). All administrative: a provider connection speaks for the
  // whole company, and its health reveals which providers the company uses.
  /** See each provider's state and health — never a credential. */
  'integration.view',
  /** Store credentials, switch a provider off or on, run its check. */
  'integration.manage',
  /** Run the webhook-processing and outbox sweep by hand. */
  'integration.process',

  // Operations (OPS-006). Administrative: it shows how the deployment is built and configured.
  'operations.diagnostics',

  // Security administration
  'security.role.view',
  'security.role.create',
  'security.role.edit',
  'security.grant.view',
  'security.grant.assign',
  /** Grant permissions or scopes the actor does not personally hold. Deliberately separate. */
  'security.grant.assignAny',

  /* ------------------------------------------------------------------------
   * Demonstration-slice permissions (Macro Phase 1, ADR-0025).
   *
   * These name verbs in modules whose engineering phase has not started. They are registered here
   * because authorization is never added retroactively: a route without a permission is a route that
   * ships open. Breadth is still the data scope, never a `viewAny` permission (ADR-0006).
   * ---------------------------------------------------------------------- */

  // Organization (`CORE-ORG`)
  'org.view',
  /** Create or change the hierarchy every data scope resolves against. Administrative. */
  'org.manage',
  'org.placement.view',
  /** Place a person, or change who they report to — which changes where approvals escalate. Administrative. */
  'org.placement.manage',

  // Inventory (`INV-*`)
  'inventory.project.view',
  'inventory.project.manage',
  'inventory.unit.view',
  /** Create units and change their status through the permitted transitions. */
  'inventory.unit.manage',
  /** Reveals a unit's pricing. Someone may need to see availability without seeing the price list. */
  'inventory.unit.viewPricing',
  /** Take units out of the system as a file (CORE-IMPORT-003). Prices only with viewPricing too. */
  'inventory.unit.export',
  /** Propose or cancel a unit price version; it takes effect through approval where a policy applies (INV-PRICE-003). */
  'inventory.price.propose',
  /** Take a timed customer hold on an available unit, and act on one's own holds (INV-HOLD-001). */
  'inventory.hold.create',
  /** Release or extend anyone's hold inside one's scope (INV-HOLD-002). */
  'inventory.hold.manage',
  /** Create and retire payment-plan templates (INV-PLAN-001). */
  'inventory.plan.manage',
  /** Onboard existing units from a file (Release 1, CORE-IMPORT): with inventory.unit.manage. */
  'inventory.unit.import',

  // CRM (`CRM-*`)
  'crm.customer.view',
  'crm.customer.manage',
  /** Reveals identity document numbers (CRM-PERSON-002). Absent without it, never masked. */
  'crm.customer.viewIdentity',
  /** Hand a customer to another owner, or name the owner when creating one (CRM-OWNER-001). */
  'crm.customer.transfer',
  'crm.lead.view',
  /** Turn a lead into a customer — the one path by which a representative creates a customer (CRM-LEAD-005). */
  'crm.lead.convert',
  /** Take leads — names and phone numbers — out of the system as a file (CORE-IMPORT-003). */
  'crm.lead.export',
  /** Bring leads in from a file, previewed and committed as one transaction (CRM-LEAD-006). */
  'crm.lead.import',
  /** Onboard existing customers from a file (Release 1, CORE-IMPORT): with crm.customer.manage. */
  'crm.customer.import',
  // Opportunities (CRM-OPP). Breadth is the data scope; the owner fields resolve `self`/`assigned`.
  'crm.opportunity.view',
  /** Open, edit and move an opportunity between the open stages, or close it as lost. */
  'crm.opportunity.manage',
  /** Hand an opportunity to another owner, or name the owner when opening one (CRM-OWNER-001). */
  'crm.opportunity.assign',
  'crm.lead.create',
  'crm.lead.edit',
  /** Hand a lead to another sales owner — a manager's action, not an owner's. */
  'crm.lead.assign',
  'crm.activity.create',

  // Sales (`SALE-*`)
  'sales.reservation.view',
  'sales.reservation.create',
  'sales.reservation.confirm',
  'sales.reservation.cancel',
  /** Extend a live reservation's validity; through approval where a policy applies (SALE-RESERVE-003). */
  'sales.reservation.extend',
  'sales.contract.view',
  'sales.contract.create',
  /** Activate a draft contract, freezing its schedule (SALE-CONTRACT-003, BD-35). */
  'sales.contract.activate',
  'sales.contract.cancel',
  /**
   * Return to sale a unit kept after its contract was cancelled (SALE-CANCEL-002): refused while the
   * contract's refund is unpaid or its instruments are still in custody. Reason and audit required.
   */
  'sales.contract.releaseUnit',
  /** Record the signing date and signed copy (SALE-CONTRACT-003). */
  'sales.contract.sign',
  /** Request a plan-change amendment; applied only through approval (SALE-CHANGE-001). */
  'sales.contract.amend',
  /** Priced offers that never reserve a unit (SALE-QUOTE-001). */
  'sales.quotation.view',
  'sales.quotation.manage',
  /**
   * Onboard historical contracts and their schedules from files (Release 1, BD-63): with
   * sales.contract.create. It commits units and creates receivables.
   */
  'sales.contract.import',

  // Collections (`COL-*`)
  'collection.installment.view',
  'collection.receipt.view',
  'collection.receipt.create',
  /** Reverse a posted receipt. Never an edit: a posted receipt is immutable (ADR-0009). */
  'collection.receipt.cancel',
  'collection.instrument.view',
  'collection.instrument.manage',
  'collection.reminder.view',
  'collection.reminder.manage',
  /**
   * Onboard receipts received before the cutover (Release 1, BD-63): with collection.receipt.create.
   * It settles instalments.
   */
  'collection.receipt.import',

  // Accounting (`FIN-COA`, `FIN-GL`, `FIN-ARAP`, `FIN-REPORT`; BMP-2, ADR-0034). Breadth is the data
  // scope over the legal entity and the branch dimension, as everywhere else.
  /** Read the chart of accounts, fiscal years, periods and posting rules. */
  'finance.account.view',
  /** Create, edit, deactivate accounts. Administrative: the chart decides where every amount lands. */
  'finance.account.manage',
  /** Create fiscal years; open, close, lock and reopen periods; close a year. Administrative. */
  'finance.period.manage',
  /** Write and retire posting rules; post queued events. Administrative (`BD-18`). */
  'finance.postingRule.manage',
  /** Read journal entries and the posting-event queue. */
  'finance.journal.view',
  /** Draft manual journal entries. */
  'finance.journal.create',
  /** Post a balanced draft to the ledger — through approval where a policy applies. */
  'finance.journal.post',
  /** Reverse a posted entry with a contra-entry. */
  'finance.journal.reverse',
  /** Trial balance, general ledger and party statements. */
  'finance.report.view',
  /** Post revenue recognised under the adopted rule, and reverse a recognition (FIN-REVREC). */
  'finance.revenue.recognize',
  /**
   * Confirm the company's historical-onboarding treatment and cutover date (BD-63), and make a
   * controlled correction of the cutover once historical records exist. Needs a recently confirmed password.
   */
  'finance.onboarding.adopt',

  // Receivables (BMP-2 package 2): credit, waivers, refunds, charges, statements, collectors.
  /** Apply a customer's unapplied credit to instalments or charges. */
  'collection.credit.apply',
  /** Ask to waive part of an instalment or a charge; applied through approval where a policy applies. */
  'collection.waiver.request',
  'collection.refund.view',
  /** Request a refund of unapplied credit. */
  'collection.refund.request',
  /** Record that an approved refund was paid. Needs a recently confirmed password: money leaves the company. */
  'collection.refund.pay',
  // Onboarding (BD-63): committing contracts and historical receipts changes what customers owe.
  'sales.contract.import',
  'collection.receipt.import',
  /** Ageing, forecast and collection performance. */
  'collection.report.view',
  /** Assign a contract's collector. */
  'collection.assignment.manage',
  /** Write collection notes and their follow-up tasks. */
  'collection.note.create',
  /** Run the late-fee sweep by hand. */
  'collection.charge.manage',
  // Customer invoices, credit and debit notes (COL-INVOICE, FIN-TAX-001).
  'finance.invoice.view',
  'finance.invoice.manage',
  /** Issue an invoice or a note: it posts and is numbered. */
  'finance.invoice.issue',

  // Banks, treasury and reconciliation (BMP-2 package 3, ADR-0036). The product records money that
  // moved at a bank or a cash desk; it never moves it (ADR-0034 §5).
  /** Read banks, treasury accounts with their masked identifiers, movements, transfers and balances. */
  'treasury.view',
  /** The bank master and its branches. */
  'treasury.bank.manage',
  /** Open, edit and close bank accounts, cashboxes and petty cash. Administrative: where money lands. */
  'treasury.account.manage',
  /** See an account number or IBAN in full. Audited; needs a recently confirmed password. */
  'treasury.account.revealIdentifiers',
  /** Record deposits, withdrawals, charges, interest, cash receipts and payments, opening balances. */
  'treasury.movement.record',
  /** Reverse a recorded movement or transfer with a contra-movement. */
  'treasury.movement.reverse',
  /** Record a transfer between two treasury accounts — through approval where a policy applies. */
  'treasury.transfer.create',
  /** Create payees and request a change of their bank details (applied only through approval). */
  'treasury.payee.manage',
  /** Request an outgoing payment; it needs an approval policy (FIN-CASH-003). */
  'treasury.payment.request',
  /** Record that an approved payment was made, and move outgoing cheques. Needs a recently confirmed password. */
  'treasury.payment.execute',
  /** Bank statements, statement lines and matching. */
  'treasury.reconciliation.manage',
  /** Approve a completed reconciliation; never the person who prepared it. */
  'treasury.reconciliation.approve',
  /** Treasury position, expected inflows and outflows. */
  'treasury.report.view',

  // Marketing (`MKT-*`). Publication is absent on purpose: no provider is connected (ADR-0026).
  'marketing.campaign.view',
  'marketing.campaign.manage',
  /** Enter, import, correct or withdraw dated performance figures (manual mode, ADR-0042). */
  'marketing.performance.record',
  /** Export the campaign report through the secure export (CORE-IMPORT-003). */
  'marketing.campaign.export',

  // Vendors, procurement and payables (BMP-2 package 4, PROC-*, FIN-INVOICE-001, FIN-ARAP-002).
  'procurement.vendor.view',
  /** Create and edit vendors, change their status, link their treasury payee. */
  'procurement.vendor.manage',
  /** See a supplier's tax and commercial registration in full, and its identity and registration papers. */
  'procurement.vendor.viewIdentifiers',
  'procurement.request.view',
  /** Raise purchase requests; the requester never approves their own (BD-50). */
  'procurement.request.create',
  /** Requests for quotation: invite vendors, record quotes, award. */
  'procurement.rfq.manage',
  'procurement.order.view',
  /** Draft, amend, submit and close purchase orders. */
  'procurement.order.manage',
  /** Record and reverse goods and service receipts against an order. */
  'procurement.receipt.record',
  'procurement.invoice.view',
  /** Record vendor invoices and ask for their payment. */
  'procurement.invoice.record',
  /** Vendor statements and payables ageing. */
  'procurement.report.view',
  /** Request an advance to a vendor against an approved order (paid through treasury, BD-59). */
  'procurement.advance.request',
  /** Release retention held on vendor invoices (BD-51; through approval where a policy governs it). */
  'procurement.retention.release',
  /**
   * Decide a direct purchase (an order without quotations) directly, within one's authority limit
   * (BD-57) — never one's own or one's own request's. Under a published policy it is decided in the inbox.
   */
  'procurement.directPurchase.approve',

  // Budgets (BMP-2 package 5, FIN-BUDGET-001…003).
  'finance.budget.view',
  /** Create budgets, prepare and submit versions, archive. Approval goes through the engine. */
  'finance.budget.manage',

  // General expenses (FIN-CASH-004).
  'finance.expense.view',
  /** Enter one's own expenses, submit and withdraw them. */
  'finance.expense.create',
  /** Decide another person's expense directly (no policy), within one's authority limit (BD-57). */
  'finance.expense.approve',
  /** Ask treasury to pay an approved expense (treasury then approves and executes, BD-59). */
  'finance.expense.pay',
  /** Cash advances (FIN-PETTY-001): see, request (for oneself or a holder), approve and review spend. */
  'finance.advance.view',
  'finance.advance.request',
  'finance.advance.approve',
  /** Recurring expense templates (FIN-CASH-006): keep them and generate the due drafts. */
  'finance.expenseTemplate.manage',

  // Cheque printing: layouts are administrative; printing changes nothing on the cheque.
  'treasury.chequeLayout.manage',
  'data.reset.request',
  'data.reset.execute',
  'treasury.cheque.print',

  // Customer portal (PORTAL-CUSTOMER-001): invite a customer, hand over links, suspend access.
  'portal.manage',

  // Controlled data reset: request (scopes, counts, reason) and execute (recently confirmed password, phrase).
  'data.reset.request',
  'data.reset.execute',

  // Warehouses and materials (BMP-2 package 4b, WH-ITEM-001, WH-TRANS-001).
  'stores.view',
  /** Items and their units. */
  'stores.item.manage',
  /** Stores and their keepers. */
  'stores.warehouse.manage',
  /** Openings, receipts, issues, returns and transfers; cancel a transfer in transit. */
  'stores.movement.record',
  /** Adjustments, counts and reversals — changes of the books without goods moving. */
  'stores.adjustment.manage',
  /** Enter counted figures on an open count; whoever counted may not approve its differences. */
  'stores.count.enter',
  /** Hand company material to someone in custody and settle it; record others' material held. */
  'stores.custody.manage',
  /** Plan, dispatch and complete deliveries (logistics only; stock moves through its documents). */
  'stores.delivery.manage',

  // Construction and contractors (BMP-2 package 4b, CONST-CONTRACT-001, CONST-CERT-001, CONST-PROGRESS-001).
  'construction.view',
  /** Contractor contracts, BOQ, variations and guarantees. */
  'construction.contract.manage',
  /** Prepare and submit progress certificates (the site engineer). */
  'construction.certificate.prepare',
  /** Approve a certificate's quantities — never the preparer. */
  'construction.certificate.engineeringApprove',
  /** Certify a certificate for payment — never the preparer or the engineering approver. */
  'construction.certificate.financeApprove',
  /** Milestones, daily reports, delays and risks. */
  'construction.progress.record',
  /** Ask treasury to pay a certificate, an advance or released retention (BD-59). */
  'construction.payment.request',

  // Fixed assets (BMP-2 package 5, FIN-ASSET-001, FIN-ASSET-002).
  'finance.asset.view',
  /** Register assets, put them in service, transfer them, record maintenance. */
  'finance.asset.manage',
  /** Asset categories and their depreciation terms (BD-42). */
  'finance.asset.configure',
  /** Prepare, post and discard monthly depreciation runs. */
  'finance.asset.depreciate',
  /** Dispose of an asset (through approval where a policy governs it). */
  'finance.asset.dispose',

  // Human resources (BMP-2 package 6, HR-EMP, HR-TIME, HR-CUSTODY).
  'hr.employee.view',
  /** Create and edit employees, contracts and checklists; move them through their lifecycle. */
  'hr.employee.manage',
  /** National identity, personal contact and emergency contact — restricted fields; reads audited. */
  'hr.employee.viewSensitive',
  'hr.attendance.view',
  'hr.attendance.record',
  'hr.leave.view',
  /** Ask for leave (for oneself or, for HR, on someone's behalf); cancel one's own request. */
  'hr.leave.request',
  /** Approve or reject leave — never one's own request (BD-45). */
  'hr.leave.approve',
  /** Leave types and entitlements (BD-45: none exist until configured). */
  'hr.leave.configure',

  // Payroll (BMP-2 package 7, HR-PAY, HR-ADV, HR-COMM).
  'hr.payroll.view',
  /** Salaries, advances, commission lines; prepare, submit and discard runs. */
  'hr.payroll.manage',
  /** Pay components (BD-44: none assumed). */
  'hr.payroll.configure',
  /** Approve a run or a commission line — never one's own. */
  'hr.payroll.approve',
  /** Post and reverse runs. */
  'hr.payroll.post',
  /** The bank-transfer file (decrypts employee bank accounts). */
  'hr.payroll.export',
  /** Ask treasury to pay an advance to an employee (with treasury.payment.request, HR-ADV-001). */
  'hr.advance.request',

  // Handover and after-sales (Phase 9, HAND-*, CS-TICKET).
  'handover.view',
  /** Open cases, readiness items, appointments, defects. */
  'handover.manage',
  /** Approve a unit as ready — never the case's owner. */
  'handover.approve',
  /** Record the delivery (signed paper filed, no open defect). */
  'handover.deliver',
  'aftersales.view',
  /** Open, assign, resolve and close customer requests. */
  'aftersales.manage',
  // 2026-10-22 — sales completion (see PERMISSION_INTRODUCED_ON).
  /** Move an active contract to another unit, carrying its received money (SALE-CHANGE-002). */
  'sales.contract.replaceUnit',
  /** Assign an active contract to another buyer (SALE-CHANGE-003). */
  'sales.contract.assign',
  /** Merge a duplicate customer into another, preview then confirm (CRM-PERSON-006). */
  'crm.customer.merge',
  /** Configure automatic lead distribution (CRM-ASSIGN-002). */
  'crm.distribution.manage',
  'sales.commission.view',
  /** Commission rules: rates, bases and effective dates (HR-COMM-002). */
  'sales.commission.manage',
  /** Approve calculated commissions, making them payable (HR-COMM-002). */
  'sales.commission.approve',
  /** Reverse an executed payment in treasury, with a reason (COL-REFUND-002). */
  'treasury.payment.reverse',
  // 2026-10-23 — archive, restore and controlled permanent deletion (ADR-0047).
  /** Archive and restore leads, customers and opportunities in the actor's scope. */
  'crm.record.archive',
  /** Permanently delete a lead, customer or opportunity entered by mistake and referenced nowhere. */
  'crm.record.deletePermanently',
  /** Archive and restore projects, buildings and units in the actor's catalogue scope. */
  'inventory.record.archive',
  /** Permanently delete a project, building or unit entered by mistake and referenced nowhere. */
  'inventory.record.deletePermanently',
  // 2026-11-02 — the Sales Admin workspace (ADR-0049).
  /** The sales team, its dashboard, follow-up view, performance reports and deals, in scope. */
  'crm.team.view',
  /** Create a Sales Representative account in the actor's scope — that job title and nothing else. */
  'crm.team.addRepresentative',
  /** Set salespeople's monthly targets, for people inside the actor's scope. */
  'crm.target.manage',
  /** CRM thresholds and the CRM reference lists; no other setting. */
  'crm.settings.manage',
] as const;

export const PermissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof PermissionSchema>;

/**
 * When a permission entered the catalog after the first production installation (2026-10-19). A role
 * that held the **whole** catalog as it stood when it was last reviewed — the installation
 * administrator's role — is offered these explicitly, to adopt with a reason; nothing is granted to
 * anyone automatically (SEC-024, SEC-031).
 */
export const PERMISSION_INTRODUCED_ON: Readonly<Partial<Record<Permission, string>>> = {
  'sales.contract.releaseUnit': '2026-10-21',
  'sales.contract.replaceUnit': '2026-10-22',
  'sales.contract.assign': '2026-10-22',
  'crm.customer.merge': '2026-10-22',
  'crm.distribution.manage': '2026-10-22',
  'sales.commission.view': '2026-10-22',
  'sales.commission.manage': '2026-10-22',
  'sales.commission.approve': '2026-10-22',
  'treasury.payment.reverse': '2026-10-22',
  'crm.record.archive': '2026-10-23',
  'crm.record.deletePermanently': '2026-10-23',
  'inventory.record.archive': '2026-10-23',
  'inventory.record.deletePermanently': '2026-10-23',
  'crm.team.view': '2026-11-02',
  'crm.team.addRepresentative': '2026-11-02',
  'crm.target.manage': '2026-11-02',
  'crm.settings.manage': '2026-11-02',
};

/**
 * Administrative permissions are explicit: they are never implied by any other permission, and a role
 * carrying one is flagged so that `SD-02` role design can see them at a glance.
 */
export const ADMINISTRATIVE_PERMISSIONS: readonly Permission[] = [
  // Approval controls: publishing a policy, moving someone else's pending decision, escalating, and
  // delegating on another account's behalf all change who may approve what.
  'approval.policy.publish',
  'approval.request.reassign',
  'approval.request.escalate',
  'approval.delegation.manageAny',
  'security.account.create',
  'security.account.suspend',
  'security.account.reactivate',
  'security.account.offboard',
  'security.account.resetPassword',
  'security.session.revokeAny',
  'security.role.create',
  'security.role.edit',
  'security.grant.assign',
  'security.grant.assignAny',
  /**
   * The company profile is what every document, screen and authenticator label is issued under, so
   * changing it is an administrative act.
   */
  'company.profile.manage',
  /** Settings and reference data decide defaults every record is created with (PLAT-024, PLAT-025). */
  'settings.manage',
  'referenceData.manage',
  /** A number format is permanent in every document issued under it (ADR-0009, `SD-10`). */
  'numbering.manage',
  /** Retention and legal hold decide what the organization keeps and for how long. */
  'document.manageRetention',
  /** A revocation tells anyone who scans the document that it no longer stands. */
  'document.revoke',
  /** A published template is the legal wording of every document generated from it. */
  'template.manage',
  'notification.viewDeliveries',
  'notification.dispatch',
  /** Moving someone's whole workload changes who is accountable for it. */
  'task.reassign',
  'task.sweep',
  /** A provider connection acts for the whole company, and its credentials are its keys. */
  'integration.view',
  'integration.manage',
  'integration.process',
  'operations.diagnostics',
  /**
   * Organization structure is administrative because **every data scope resolves against it**
   * (SEC-026) and the reporting line decides where an overdue approval escalates (`APPROVAL-005`).
   * Moving a team between branches silently widens what its members can see, which is an
   * authorization change wearing an org-chart costume.
   */
  'org.manage',
  'org.placement.manage',
  /** The chart, the periods and the posting map decide where every amount in the ledger lands (ADR-0034). */
  'finance.account.manage',
  'finance.period.manage',
  'finance.postingRule.manage',
  /** A treasury account decides where money is recorded as held, and carries encrypted bank data. */
  'treasury.account.manage',
  'treasury.chequeLayout.manage',
  /** Permanent deletion removes a record for good: only a mistaken, unreferenced one (ADR-0047). */
  'crm.record.deletePermanently',
  'inventory.record.deletePermanently',
];

/**
 * Operations that need the password entered again within the last few minutes (ADR-0046).
 *
 * The owner removed the mandatory second factor on 2026-10-17: every account signs in with its
 * password alone. What remains is a step-up for the handful of operations where a hijacked, unattended
 * session would do the most harm — money leaving, an account number read in full, salaries exported for
 * the bank, history tied to the ledger, and the controlled data reset. The route guard
 * `requireRecentPassword` enforces it on the server; this list documents which routes carry it and lets
 * the interface say so. It is **not** a second factor and nothing records it as one. Approvals,
 * segregation of duties and the reset's own safeguards are unchanged.
 */
export const PASSWORD_CONFIRMATION_PERMISSIONS: readonly Permission[] = [
  'treasury.payment.execute',
  'treasury.payment.reverse',
  'treasury.account.revealIdentifiers',
  'collection.refund.pay',
  'hr.payroll.export',
  'finance.onboarding.adopt',
  'data.reset.execute',
  'crm.record.deletePermanently',
  'inventory.record.deletePermanently',
];

/** How long a password entry counts as recent for those operations. */
export const PASSWORD_CONFIRMATION_WINDOW_SECONDS = 600;

export function isAdministrativePermission(permission: Permission): boolean {
  return ADMINISTRATIVE_PERMISSIONS.includes(permission);
}

/**
 * Data scope levels (SEC-026, MASTER-MAPPING §6), ordered from narrowest to widest. The order is used
 * to stop an actor from granting a scope wider than their own (SEC-031).
 */
export const SCOPE_LEVELS = [
  'self',
  'assigned',
  'team',
  'department',
  'branch',
  'project',
  'legalEntity',
  'all',
] as const;

export const ScopeLevelSchema = z.enum(SCOPE_LEVELS);
export type ScopeLevel = z.infer<typeof ScopeLevelSchema>;

export function scopeRank(level: ScopeLevel): number {
  return SCOPE_LEVELS.indexOf(level);
}

/**
 * Organization references the scope resolves against. The values are owned by `CORE-ORG` (Phase 2) and
 * are opaque identifiers here — `SEC` stores the assignment, never the hierarchy (ADR-0019).
 */
export const ScopeAssignmentSchema = z.strictObject({
  level: ScopeLevelSchema,
  teamIds: z.array(z.string().min(1)).max(200).default([]),
  departmentIds: z.array(z.string().min(1)).max(200).default([]),
  branchIds: z.array(z.string().min(1)).max(200).default([]),
  projectIds: z.array(z.string().min(1)).max(500).default([]),
  legalEntityIds: z.array(z.string().min(1)).max(50).default([]),
});
export type ScopeAssignment = z.infer<typeof ScopeAssignmentSchema>;

/**
 * The actor context is built **on the server** from stored grants on every request. Nothing in it is
 * ever taken from the client: a request that claims roles, permissions, or ownership is ignored.
 */
export const ActorContextSchema = z.strictObject({
  /** Opaque reference to a security account (`SEC-011`). Never an employee record (ADR-0019). */
  accountId: z.string().min(1),
  kind: z.enum(['account', 'system']).default('account'),
  roleKeys: z.array(z.string().min(1)).default([]),
  permissions: z.array(PermissionSchema).default([]),
  /** Explicit denials. Deny always wins over a grant (SEC-025). */
  deniedPermissions: z.array(PermissionSchema).default([]),
  scope: ScopeAssignmentSchema,
  /** The account still holds its initial password: nothing but changing it is allowed (ADR-0049). */
  passwordChangeRequired: z.boolean().optional(),
  sessionId: z.string().min(1).optional(),
  /**
   * When the password was last entered on this session (sign-in or confirmation, ADR-0046). Set by the
   * server from the stored session; never read from a request.
   */
  passwordConfirmedAt: z.string().min(1).max(40).optional(),
  /** Increments on every grant change, so a change is observable (SEC-032). */
  grantVersion: z.number().int().nonnegative().default(0),
});
export type ActorContext = z.infer<typeof ActorContextSchema>;

/** A role is a named bundle of permissions — administrative convenience only (SEC-024). */
export const RoleKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{1,63}$/, { message: 'ROLE_KEY_EXPECTED' });

export const RoleSchema = z.strictObject({
  key: RoleKeySchema,
  name: LocalizedLabelSchema,
  permissions: z.array(PermissionSchema).max(PERMISSIONS.length),
  isAdministrative: z.boolean(),
  version: z.number().int().positive(),
  /** The day the role's permission set was last reviewed against the catalog (adoption). */
  permissionsReviewedOn: z.string().optional(),
  /**
   * Permissions introduced since then that this role may adopt explicitly — only for a role that held
   * the whole catalog as it then stood (the installation administrator's). Computed on read.
   */
  newPermissionsAvailable: z.array(PermissionSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Role = z.infer<typeof RoleSchema>;

/**
 * Change an existing role (SEC-024): its name and its whole permission set, against the version the
 * editor read. Nobody adds to a role they hold permissions they do not hold themselves, and the last
 * usable administrator is never removed (SEC-031).
 */
export const UpdateRoleRequestSchema = z.strictObject({
  name: LocalizedLabelSchema.optional(),
  permissions: z.array(PermissionSchema).min(1).max(PERMISSIONS.length),
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});
export type UpdateRoleRequest = z.infer<typeof UpdateRoleRequestSchema>;

/** Adopt some or all of a role's newly introduced permissions, explicitly and with a reason. */
export const AdoptRolePermissionsSchema = z.strictObject({
  permissions: z.array(PermissionSchema).max(PERMISSIONS.length),
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});
export type AdoptRolePermissions = z.infer<typeof AdoptRolePermissionsSchema>;

export const CreateRoleRequestSchema = z.strictObject({
  key: RoleSchema.shape.key,
  name: LocalizedLabelSchema,
  permissions: z.array(PermissionSchema).min(1).max(PERMISSIONS.length),
});
export type CreateRoleRequest = z.infer<typeof CreateRoleRequestSchema>;

export const RoleListResponseSchema = z.strictObject({ items: z.array(RoleSchema) });

/** What an account is granted: roles, explicit denials, and one data scope. */
export const AccountGrantSchema = z.strictObject({
  accountId: z.string().min(1),
  roleKeys: z.array(z.string().min(1)).max(50),
  deniedPermissions: z.array(PermissionSchema).max(PERMISSIONS.length),
  scope: ScopeAssignmentSchema,
  version: z.number().int().positive(),
  updatedAt: z.string(),
  updatedBy: z.string().min(1),
});
export type AccountGrant = z.infer<typeof AccountGrantSchema>;

export const SetAccountGrantRequestSchema = z.strictObject({
  roleKeys: z.array(z.string().min(1)).max(50),
  deniedPermissions: z.array(PermissionSchema).max(PERMISSIONS.length).default([]),
  scope: ScopeAssignmentSchema,
});
export type SetAccountGrantRequest = z.infer<typeof SetAccountGrantRequestSchema>;

/** Resources that carry field-level restrictions (SEC-029). */
export const RESTRICTED_RESOURCES = [
  'auditEvent',
  'accountGrant',
  'approvalRequest',
  'unit',
  'customer',
  'contract',
] as const;
export type RestrictedResource = (typeof RESTRICTED_RESOURCES)[number];

/**
 * Field → permission required to see it. A field the actor may not see is **absent** from the payload,
 * never `null` and never masked client-side (SEC-029, ADR-0006).
 */
export const FIELD_RESTRICTIONS: Readonly<
  Record<RestrictedResource, Readonly<Record<string, Permission>>>
> = {
  auditEvent: {
    changes: 'audit.viewChanges',
    'context.ip': 'audit.viewContext',
    'context.userAgent': 'audit.viewContext',
  },
  accountGrant: {
    /**
     * The explicit denial list is a policy detail: reading a grant shows which roles and scope an
     * account has, but seeing how the policy was narrowed requires the administrative permission that
     * can change it.
     */
    deniedPermissions: 'security.grant.assign',
  },
  approvalRequest: {
    /**
     * Master Mapping §6 lists financial values among the field-controlled classes. Someone may need to
     * see that an approval is outstanding without seeing the amount that triggered it.
     */
    'context.amount': 'approval.request.viewAmounts',
    'context.percentage': 'approval.request.viewAmounts',
    // The reviewed subject carries the amount too (payments, endorsements): the same rule applies.
    'subject.amount': 'approval.request.viewAmounts',
  },
  unit: {
    /**
     * Availability and pricing are different questions. A receptionist confirming that a unit is free
     * does not need the price list, and a price list is the most commercially sensitive thing inventory
     * holds. Absent, never masked — a masked price is still serialized (SEC-029).
     */
    basePrice: 'inventory.unit.viewPricing',
    currentPrice: 'inventory.unit.viewPricing',
    pricePerSquareMeter: 'inventory.unit.viewPricing',
  },
  customer: {
    /**
     * Master Mapping §6 names identity among the protected classes. A representative confirming an
     * appointment needs the phone number, not the national ID (CRM-PERSON-002).
     */
    identity: 'crm.customer.viewIdentity',
  },
  contract: {
    /** The buyer's identity snapshot is restricted exactly as the customer's identity is. */
    'customerSnapshot.identity': 'crm.customer.viewIdentity',
  },
};
